import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseManagePasswordComponent } from '@app/components/_shared/account/base-manage-password/base-manage-password.component';
import { LoadingProvider } from '@app/components/_shared/loading-spinner/loading-spinner.component';
import { CountryModel } from '@app/models/user/country.model';
import { GenderModel } from '@app/models/user/gender.model';
import { DisplayLanguageModel } from '@app/models/user/language.model';
import { UserModel } from '@app/models/user/user.model';
import { AuthService } from '@app/services/auth.service';
import { LocalisationService } from '@app/services/localisation.service';
import { UserService } from '@app/services/user.service';
import * as _ from 'lodash';

import { AppConfig } from '../../../app.config';
import { equalsValidator } from '../../../directives/equals-validator.directive';
import { Globals } from './../../../../globals';

@Component({
  selector: 'app-account-settings',
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.scss']
})
export class AccountSettingsComponent extends BaseManagePasswordComponent implements OnInit {
  @ViewChild(BaseManagePasswordComponent)
  child; // only needed if parent need to interact with Child

  static route = 'account/settings';

  // BaseManagePasswordComponent template variables to be passed
  templateSettings = {
    headerLegend: true,
    showMessagesBottom: true,
    redirectOnSuccess: false
  };

  locale: any = {};

  // to disable the settings
  isDaraUser: boolean = false;

  profileForm: FormGroup = new FormGroup({});
  profileFormSubmitted: boolean = false;

  passwordForm: FormGroup = new FormGroup({});
  passwordFormSubmitted: boolean = false;
  passwordLength: number;

  countryOptions: CountryModel[] = [];
  genderOptions: GenderModel[];
  displayLanguageOptions: DisplayLanguageModel[] = [];

  messageCopyProfile: string;
  messageCopyPassword: string;
  messageClassProfile: string;
  messageClassPassword: string;

  public formSubmitted: boolean = false;
  languageChanged: boolean = false;

  constructor(
    globals: Globals,
    route: ActivatedRoute,
    router: Router,
    localisation: LocalisationService,
    authService: AuthService,
    formBuilder: FormBuilder,
    private userService: UserService,
    private loading: LoadingProvider
  ) {
    super(globals, route, router, localisation, authService, formBuilder); // Reflects the base-manage-password component constuctor
  }

  ngOnInit() {
    this.locale = this.localisation.getTranslationsForComponent('AccountSettingsComponent');
    this.loading.show(this.locale.findingYourSettings, true);
    this.passwordLength = AppConfig.passwordLength;

    if (sessionStorage.getItem('completed') === 'true') {
      sessionStorage.removeItem('completed');
      location.reload();
    }

    this.userService
      .getCountries()
      .subscribe(
        countries =>
          (this.countryOptions = _.orderBy(countries, ['priority', 'countryName'], 'asc'))
      );

    this.userService.getGenders().subscribe(genders => (this.genderOptions = genders));
    this.userService
      .getDisplayLanguages()
      .subscribe(languages => (this.displayLanguageOptions = languages));

    this.profileForm = this.formBuilder.group({
      firstName: [null, Validators.required],
      secondName: [null, Validators.required],
      email: [null, [Validators.required, Validators.pattern(AppConfig.emailRegex)]],
      gender: [null, Validators.required],
      school: [null],
      displayLanguage: [null, Validators.required],
      emailPreference: [true, Validators.required],
      country: [null, [Validators.required]]
    });

    this.passwordForm = this.formBuilder.group(
      {
        currentPassword: [null, [Validators.required]],
        newPassword: [null, [Validators.required, Validators.minLength(this.passwordLength)]],
        confirmPassword: [null, [Validators.required, Validators.minLength(this.passwordLength)]]
      },
      {
        validator: equalsValidator('newPassword', 'confirmPassword')
      }
    );

    this.userService.getUser().subscribe(
      user => {
        if (user.daraUserId) {
          this.isDaraUser = true;
        }
        let formModel = new UserModel();
        formModel.firstName = user.firstName;
        formModel.secondName = user.secondName;
        formModel.email = user.email;
        formModel.gender = user.gender;
        formModel.country = user.country;
        formModel.school = user.school;
        formModel.displayLanguage = user.displayLanguage;
        formModel.emailPreference = user.emailPreference;

        this.profileForm.setValue(formModel);
      },
      err => {},
      () => {
        this.loading.hide();
      }
    );
  }

  updateLanguageChanged() {
    this.languageChanged = true;
  }

  setFormSubmitted(value) {
    this.formSubmitted = value;
  }

  onClickProfileInput() {
    this.messageCopyProfile = null;
  }

  onClickPasswordInput() {
    this.messageCopyPassword = null;
  }

  goBack() {
    window.history.back();
  }

  submitProfileForm() {
    this.profileFormSubmitted = true;

    if (this.profileForm.valid) {
      let values = this.profileForm.value;
      this.setFormSubmitted(true);

      this.userService.updateUser(values).subscribe(
        result => {
          this.messageClassProfile = this.globals.styles.alertBottomClass;

          if (this.languageChanged == true) {
            this.localisation
              .updateCurrentLanguage(this.profileForm.value.displayLanguage)
              .then(data => {
                this.languageChanged = false;
                this.locale = this.localisation.getTranslationsForComponent(
                  'AccountSettingsComponent'
                );

                this.messageCopyProfile = this.locale.profileSuccessModalMessage;
                this.child.callLocale();
              });
          } else {
            this.messageCopyProfile = this.locale.profileSuccessModalMessage;
          }
        },
        error => {},
        () => {
          this.setFormSubmitted(false);
        }
      );
    } else {
      this.messageClassProfile = this.globals.styles.errorClass;
      this.messageCopyProfile = this.locale.errorModalMessage;
      this.passwordFormSubmitted = false;
    }
  }

  submitPasswordForm() {
    this.passwordFormSubmitted = true;

    if (!this.passwordForm.valid) return;

    let form = this.passwordForm.value;
    let model = new UserModel();
    model.currentPassword = form.currentPassword;
    model.password = form.newPassword;

    this.setFormSubmitted(true);
    this.userService.updateUser(model).subscribe(
      result => {
        this.messageClassPassword = this.globals.styles.alertBottomClass;
        this.messageCopyPassword = this.locale.passwordSuccessModalMessage;
        this.passwordForm.reset();
      },
      err => {
        let errorDetails = err.json();
        this.messageClassPassword = this.globals.styles.errorClass;

        // Popup diabled, so check see to if httpinterceptor returns errors if NOT revert to default message
        if (errorDetails.returnCode.hasOwnProperty('errors')) {
          this.messageCopyPassword =
            errorDetails.returnCode.errors[Object.keys(errorDetails.returnCode.errors)[0]];
        } else if (errorDetails.returnCode.hasOwnProperty('message')) {
          this.messageCopyPassword = errorDetails.returnCode.message;
        } else {
          this.messageCopyPassword = this.locale.errorModalMessage;
        }
      },
      () => {
        this.passwordFormSubmitted = false;
        this.setFormSubmitted(false);
      }
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/components/account/settings/account-settings.component.ts